// Canvas primitives for map markers. Each mirrors the divIcon markup in
// mapIcons.js (same radii, stroke widths and scales), so an exported image
// looks like the on-screen map. Knows shapes, nothing about layers.

export const TRACK = 'rgba(0,0,0,0.15)';
const DOT_STROKE = '#ffffff';
const LABEL_FONT = '500 11px system-ui, sans-serif';
const TAU = Math.PI * 2;

export function drawDot(ctx, point, radius, color) {
  ctx.save();
  ctx.globalAlpha = 0.85;
  ctx.beginPath();
  ctx.arc(point.x, point.y, radius, 0, TAU);
  ctx.fillStyle = color;
  ctx.fill();
  ctx.lineWidth = 2;
  ctx.strokeStyle = DOT_STROKE;
  ctx.stroke();
  ctx.restore();
}

// Fraction of a ring, drawn from 12 o'clock clockwise like the SVG dasharray.
export function drawRing(ctx, point, radius, width, fraction, color, track = TRACK) {
  strokeTrack(ctx, point, radius, width, track);
  if (fraction <= 0) return;
  ctx.save();
  ctx.beginPath();
  ctx.arc(point.x, point.y, radius, -Math.PI / 2, -Math.PI / 2 + Math.min(1, fraction) * TAU);
  ctx.lineWidth = width;
  ctx.strokeStyle = color;
  ctx.stroke();
  ctx.restore();
}

// Multi-segment ring: one arc per part with a positive value, in order.
export function drawDonut(ctx, point, parts, colors, scale) {
  const radius = 8 * scale;
  const width = 12 * scale;
  strokeTrack(ctx, point, radius, width);
  const total = parts.reduce((sum, part) => sum + (Number(part.value) || 0), 0);
  if (total <= 0) return;
  let offset = 0;
  parts.forEach((part, index) => {
    const value = Number(part.value) || 0;
    if (value <= 0) return;
    const sweep = (value / total) * TAU;
    ctx.save();
    ctx.beginPath();
    ctx.arc(point.x, point.y, radius, -Math.PI / 2 + offset, -Math.PI / 2 + offset + sweep);
    ctx.lineWidth = width;
    ctx.strokeStyle = colors[index % colors.length];
    ctx.stroke();
    ctx.restore();
    offset += sweep;
  });
}

// Vertical bar anchored by its bottom edge, like the 16x36 bar icon.
export function drawBar(ctx, point, fraction, scale, color) {
  const height = Math.max(4, Math.min(1, fraction) * 32) * scale;
  ctx.save();
  ctx.fillStyle = color;
  ctx.fillRect(point.x - 6 * scale, point.y - height, 12 * scale, height);
  ctx.restore();
}

export function drawLabel(ctx, point, text, color) {
  if (!text) return;
  ctx.save();
  ctx.font = LABEL_FONT;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.lineJoin = 'round';
  ctx.lineWidth = 3;
  ctx.strokeStyle = 'rgba(255,255,255,0.9)';
  ctx.strokeText(text, point.x, point.y);
  ctx.fillStyle = color;
  ctx.fillText(text, point.x, point.y);
  ctx.restore();
}

// Base layer attribution, required by the tile providers' licenses.
export function drawAttribution(ctx, attribution, width, height) {
  const text = String(attribution || '')
    .replace(/&copy;/g, '©')
    .replace(/&nbsp;/g, ' ')
    .replace(/<[^>]*>/g, '');
  if (!text.trim()) return;
  ctx.save();
  ctx.font = '10px system-ui, sans-serif';
  ctx.textAlign = 'right';
  ctx.textBaseline = 'bottom';
  const metrics = ctx.measureText(text);
  ctx.fillStyle = 'rgba(255,255,255,0.7)';
  ctx.fillRect(width - metrics.width - 8, height - 16, metrics.width + 8, 16);
  ctx.fillStyle = '#333333';
  ctx.fillText(text, width - 4, height - 4);
  ctx.restore();
}

function strokeTrack(ctx, point, radius, width, color = TRACK) {
  ctx.save();
  ctx.beginPath();
  ctx.arc(point.x, point.y, radius, 0, TAU);
  ctx.lineWidth = width;
  ctx.strokeStyle = color;
  ctx.stroke();
  ctx.restore();
}
